import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from "@/components/ui/button";
import logger from '@/lib/logger';

interface GoogleAuthButtonProps {
  mode?: 'login' | 'signup';
}

export default function GoogleAuthButton({ mode = 'login' }: GoogleAuthButtonProps) {
  const { signInWithGoogle } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    logger.info('Initiating Google sign-in', { module: 'GoogleAuthButton' });
    setIsLoading(true);
    
    // Show a message to the user about being redirected 
    toast({
      title: "Redirecting to Google",
      description: "You'll be redirected to Google to sign in securely",
      duration: 3000,
    });
    
    try {
      // Add a small delay to allow the toast to show before redirect
      setTimeout(async () => {
        try {
          await signInWithGoogle();
          logger.info('Google sign-in flow initiated', { module: 'GoogleAuthButton' });
        } catch (error: any) {
          logger.error('Google sign-in failed', { 
            module: 'GoogleAuthButton',
            data: error
          });
          setIsLoading(false);
          toast({
            title: "Google sign-in failed",
            description: error.message || "An error occurred during Google sign-in",
            variant: "destructive",
          });
        }
      }, 500);
    } catch (error: any) {
      logger.error('Failed to start Google sign-in', { 
        module: 'GoogleAuthButton',
        data: error
      });
      setIsLoading(false);
      toast({
        title: "Google sign-in failed",
        description: error.message || "An error occurred during Google sign-in",
        variant: "destructive",
      });
    }
  };

  const buttonText = mode === 'signup' 
    ? (isLoading ? "Connecting..." : "Sign up with Google")
    : (isLoading ? "Connecting..." : "Sign in with Google");

  return (
    <Button 
      variant="outline" 
      className="w-full bg-white hover:bg-white border-2 border-black text-black py-2 font-bold relative overflow-hidden transition-all flex items-center justify-center gap-2 transform hover:translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] group"
      onClick={handleGoogleSignIn}
      disabled={isLoading}
    >      
      {/* Glowing colored borders - reduced opacity */}
      <div className="absolute inset-0 p-[2px] overflow-hidden pointer-events-none opacity-0 group-hover:opacity-50">
        <div className="absolute inset-0 transform rotate-0 group-hover:rotate-180 transition-transform duration-1000 ease-in-out">
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#4285F4] to-transparent absolute top-0 left-0"></div>
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#DB4437] to-transparent absolute bottom-0 left-0"></div>
          <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-[#F4B400] to-transparent absolute left-0 top-0"></div>
          <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-[#0F9D58] to-transparent absolute right-0 top-0"></div>
        </div>
      </div>
      
      {/* Moving data dots - reduced opacity */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-[10%] w-1 h-1 bg-[#4285F4] rounded-full animate-data-packet opacity-0 group-hover:opacity-30" style={{ '--path-x': '80%', '--path-y': '90%', animationDelay: '0s' } as React.CSSProperties}></div>
        <div className="absolute bottom-0 right-[10%] w-1 h-1 bg-[#DB4437] rounded-full animate-data-packet opacity-0 group-hover:opacity-30" style={{ '--path-x': '-80%', '--path-y': '-90%', animationDelay: '0.4s' } as React.CSSProperties}></div>
        <div className="absolute top-[50%] right-[5%] w-1 h-1 bg-[#F4B400] rounded-full animate-data-packet opacity-0 group-hover:opacity-30" style={{ '--path-x': '-70%', '--path-y': '20%', animationDelay: '0.8s' } as React.CSSProperties}></div>
        <div className="absolute bottom-[50%] left-[5%] w-1 h-1 bg-[#0F9D58] rounded-full animate-data-packet opacity-0 group-hover:opacity-30" style={{ '--path-x': '70%', '--path-y': '-20%', animationDelay: '1.2s' } as React.CSSProperties}></div>
      </div>
      
      {/* Google SVG icon and text with improved contrast */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24px" height="24px" className="relative z-10">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
      </svg>
      <span className="font-bold relative z-10 text-black group-hover:text-black">{buttonText}</span>
    </Button>
  );
}
